import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Calendar, User, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_MAP = {
  initiated: { label: 'Pokrenuto', color: 'text-blue-600', bg: 'bg-blue-50', icon: MessageSquare },
  in_progress: { label: 'U toku', color: 'text-amber-600', bg: 'bg-amber-50', icon: Clock },
  agreed: { label: 'Dogovoreno', color: 'text-teal-600', bg: 'bg-teal-50', icon: CheckCircle },
  completed: { label: 'Završeno', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle },
  rejected: { label: 'Odbijeno', color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle },
  cancelled: { label: 'Otkazano', color: 'text-gray-600', bg: 'bg-gray-50', icon: X },
};

export default function HistoryModal({ offer, onClose }) {
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (offer?.id) fetchHistory();
  }, [offer?.id]);

  const fetchHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        ?.from('negotiations')
        ?.select('id, status, created_at, updated_at, initiator_id, offer_id')
        ?.eq('offer_id', offer?.id)
        ?.order('created_at', { ascending: false });
      if (err) throw err;
      setNegotiations(data || []);
    } catch (e) {
      setError('Greška pri učitavanju istorije: ' + e?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Istorija pregovaranja</h2>
            <p className="text-sm text-gray-500 mt-0.5">{offer?.voucherName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
          {!loading && !error && negotiations?.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Nema zabeleženih pregovaranja za ovaj oglas</p>
            </div>
          )}
          {!loading && negotiations?.length > 0 && (
            <div className="space-y-3">
              {negotiations?.map((neg) => {
                const statusConf = STATUS_MAP?.[neg?.status] || STATUS_MAP?.initiated;
                const StatusIcon = statusConf?.icon;
                return (
                  <div key={neg?.id} className={`p-4 rounded-xl border ${statusConf?.bg} border-gray-200`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${statusConf?.color}`}>
                        <StatusIcon size={14} />
                        {statusConf?.label}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(neg?.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <User size={12} />
                      <span>ID pregovaranja: {neg?.id?.slice(0, 8)}...</span>
                    </div>
                    {neg?.updated_at && neg?.updated_at !== neg?.created_at && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
                        <Calendar size={12} />
                        <span>Ažurirano: {formatDate(neg?.updated_at)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Zatvori
          </button>
        </div>
      </div>
    </div>
  );
}
